import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Flame } from "lucide-react";

const MEMORIAL_ILLUSTRATION = "/portraits/wing_memorials.png";

const TRIBUTES = [
  {
    name: "For Nana — Margaret Bennett",
    place: "Kent, England · 1928 – 2015",
    line: "The garden still smells of you, especially in early spring.",
    from: "William, Canterbury",
  },
  {
    name: "For Nonno — Giuseppe Rossi",
    place: "Firenze, Italy · 1932 – 2012",
    line: "Your record collection is still turning. Every song remembers you.",
    from: "Sofia, Rome",
  },
  {
    name: "For Oma — Greta Hoffmann",
    place: "Munich, Germany · 1934 – 2020",
    line: "Your handwritten letters live on in our family group chat — three generations, one voice.",
    from: "Lena, Munich",
  },
];

const ease = [0.22, 1, 0.36, 1];

export default function CelebrateALife({ compact = false }) {
  return (
    <section
      data-testid="celebrate-a-life-section"
      className="relative max-w-[1400px] mx-auto px-6 md:px-10 py-16 md:py-24"
    >
      <div className="text-center mb-10 md:mb-14">
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, ease }}
          className="inline-flex flex-col items-center gap-2"
        >
          <div className="flourish w-56 md:w-72" />
          <h2 className="heritage-title text-4xl md:text-5xl lg:text-6xl">Celebrate a Life — Memorials</h2>
          <p className="mt-2 max-w-2xl mx-auto text-[15px] md:text-base font-italic-serif italic text-[color:var(--ink-soft)]">
            Living tributes for those we have lost — obituaries, remembrances and small candles
            kept alive by family and community, one page at a time.
          </p>
          <div className="flourish w-56 md:w-72" />
        </motion.div>
      </div>

      <div className="grid lg:grid-cols-12 gap-10 items-center">
        {/* Illustration */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, ease }}
          className="lg:col-span-5 order-2 lg:order-1"
        >
          <div className="vintage-frame">
            <div className="aspect-[4/5] overflow-hidden">
              <img src={MEMORIAL_ILLUSTRATION} alt="Memorial candles" />
            </div>
            <div className="absolute bottom-2 left-0 right-0 text-center font-italic-serif italic text-[12px] text-[color:var(--sepia)]">
              A candle that never burns out
            </div>
          </div>
        </motion.div>

        {/* Tributes */}
        <div className="lg:col-span-7 order-1 lg:order-2 space-y-6">
          {TRIBUTES.map((t, i) => (
            <motion.article
              key={i}
              initial={{ opacity: 0, y: 14 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1, duration: 0.7, ease }}
              className="border-l-2 border-[color:var(--rust)] pl-5 py-2"
              data-testid={`celebrate-tribute-${i}`}
            >
              <div className="flex items-center gap-2 overline text-[color:var(--sepia)]">
                <Flame size={12} className="text-[color:var(--rust)]" />
                {t.place}
              </div>
              <h3 className="mt-1 heritage-title text-2xl md:text-3xl">{t.name}</h3>
              <p className="mt-2 font-italic-serif italic text-[color:var(--ink-soft)] text-lg leading-snug">
                "{t.line}"
              </p>
              <div className="mt-2 numeric-index text-[color:var(--ink-soft)]">— {t.from}</div>
            </motion.article>
          ))}

          {!compact && (
            <div className="pt-4 flex flex-wrap gap-3">
              <Link to="/category/memorials" data-testid="celebrate-cta" className="btn-wood">
                Open a memorial
              </Link>
              <Link to="/app/register" className="btn-ghost">
                Begin your archive
              </Link>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
